<script setup lang="ts">

import { faComment } from '@fortawesome/pro-regular-svg-icons'

import { BadgeActivityStatus, BadgeBookingStatus, BadgeReservationType, BadgeSlotStatus, BadgeTutorCategory } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { AppTooltip } from '@shared/ui/tooltips'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LinkTextPrimary } from '@shared/ui/links'
import { AppIcon } from '@shared/ui/icons'

import SlotActions from './SlotActions.vue'
import SelectKid from './SelectKid.vue'
import { useTableSlotsTracker } from '../../model/shift-control/table-slots-tracker.hook'

defineOptions({
  name: 'TableSlotsTracker',
})

const {
  slots,
  isLoading,
  hasSlots,
  meta,
  formatTime,
  isCheckInLate,
  getTutorHref,
  getBookingHref,
  setChangePage,
  setChangePerPage,
  isShowStatusBadge,
} = useTableSlotsTracker()
</script>

<template>
  <div class="table-slots-tracker">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh class="view-min" />
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Tutor
            </AppText>
          </AppTh>
          <AppTh class="view-md">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Reservation
            </AppText>
          </AppTh>
          <AppTh class="view-md">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Check-in time
            </AppText>
          </AppTh>
          <AppTh class="col-slot-status">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Slot status
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Kid
            </AppText>
          </AppTh>
          <AppTh class="col-booking-num">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Booking #
            </AppText>
          </AppTh>
          <AppTh class="col-booking-status">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Booking status
            </AppText>
          </AppTh>
        </AppTr>
      </template>

      <template
        v-if="hasSlots"
        #body
      >
        <AppTr
          v-for="slot in slots"
          :key="slot.id"
        >
          <AppTd class="view-min zero-padding">
            <SlotActions :tutor-slot="slot" />
          </AppTd>
          <AppTd class="medium">
            <div class="table-slots-tracker__tutor-cell">
              <LinkTextPrimary
                v-if="slot.tutor"
                :href="getTutorHref(slot.tutor.id)"
                target="_blank"
              >
                {{ slot.tutor.full_name }}
              </LinkTextPrimary>
              <AppText
                v-else
                name="body2"
                color="slate-900"
              >
                —
              </AppText>
              <BadgeTutorCategory
                v-if="slot.tutor"
                :categories="slot.tutor.categories"
              />
            </div>
          </AppTd>
          <AppTd class="medium">
            <BadgeReservationType
              v-if="slot.reservation_type"
              :status="slot.reservation_type"
            />
            <AppText
              v-else
              name="body2"
              color="slate-900"
            >
              —
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              v-if="slot.checked_in_at"
              name="body1"
              :color="isCheckInLate(slot) ? 'red-600' : 'slate-900'"
            >
              {{ formatTime(slot.checked_in_at) }}
            </AppText>
            <AppText
              v-else
              name="body1"
              color="slate-400"
            >
              No check-in
            </AppText>
          </AppTd>
          <AppTd class="medium col-slot-status">
            <BadgeSlotStatus :status="slot.status" />
          </AppTd>
          <AppTd class="y-zero-padding">
            <div class="table-slots-tracker__kid-cell">
              <SelectKid :tutor-slot="slot" />
              <BadgeActivityStatus
                v-if="isShowStatusBadge(slot)"
                :status="slot.booking!.activity_status"
              />
            </div>
          </AppTd>
          <AppTd class="medium col-booking-num">
            <LinkTextPrimary
              v-if="slot.booking"
              :href="getBookingHref(slot.booking.discipline_id, slot.booking.id)"
              target="_blank"
            >
              {{ slot.booking.id }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body2"
              color="slate-900"
            >
              —
            </AppText>
          </AppTd>
          <AppTd class="medium col-booking-status">
            <div
              v-if="slot.booking"
              class="table-slots-tracker__booking-status-cell"
            >
              <BadgeBookingStatus
                :status="slot.booking.status"
                :category="slot.booking.reason_category ?? undefined"
              />
              <AppTooltip
                v-if="slot.booking.reason"
                :text="`Reason: ${slot.booking.reason}`"
              >
                <AppIcon :icon="faComment" />
              </AppTooltip>
            </div>
            <AppText
              v-else
              name="body2"
              color="slate-900"
            >
              —
            </AppText>
          </AppTd>
        </AppTr>
      </template>
    </AppTable>

    <div
      v-if="!hasSlots && !isLoading"
      class="table-slots-tracker__empty"
    >
      <AppText
        name="body2"
        color="slate-500"
      >
        No slots found.
      </AppText>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./TableSlotsTracker.scss" />
